/*
 * Zooltjes add-on (It's Perfect model) — own buttons, variant-independent.
 * ------------------------------------------------------------
 * Renders its own "Pakket" buttons (styled like the native block options). When
 * "Met Dr. Worker zooltjes" is chosen:
 *   - the PDP price shows shoe + net insole price (€149 -> €169),
 *   - on add-to-cart, the shoe (current variant) + the matching loose insole are added.
 * A Shopify "Buy X get Y" automatic discount (€25 amount off each insole) makes the
 * charged total match the shown €169.
 *
 * If the old "Pakket" variant option still exists, it is hidden (Liquid) and forced
 * to "Enkel werkschoen" here, so the base variant/price is always the plain shoe.
 * Once that variant is deleted, this keeps working unchanged.
 */
class ZooltjesAddon extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section') || document;
    this.form = this.closest('form[action*="/cart/add"]') ||
      this.scope.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.sizeOption = this.dataset.sizeOption || 'Schoenmaat';
    this.surcharge = parseInt(this.dataset.surcharge, 10) || 0;
    this.showPrice = this.dataset.showPrice !== 'false';
    this.insoles = this.parseInsoles();

    this.enkelRadio = this.querySelector('.zooltjes-addon__radio[value="enkel"]');
    this.metRadio = this.querySelector('.zooltjes-addon__radio[value="met"]');
    this.label = this.querySelector('.form__label__value');

    // If the legacy Pakket variant still exists, pin it to the plain-shoe value.
    this.forceBaseVariant();

    this.querySelectorAll('.zooltjes-addon__radio').forEach((r) => {
      r.addEventListener('change', () => { this.updateLabel(); this.applyPrice(); });
    });

    // Size changes: keep "Met" disabled when the matching insole is sold out, and
    // re-apply the price surcharge (theme re-renders the price on variant change).
    this.scope.addEventListener('change', () => setTimeout(() => {
      this.syncMetAvailability();
      this.applyPrice();
    }, 0));

    // Intercept add-to-cart (capture phase, before the theme handler).
    this.form.addEventListener('submit', (e) => this.onSubmit(e), true);

    this.updateLabel();
    this.syncMetAvailability();
    this.initPriceLayer();
  }

  parseInsoles() {
    const node = this.querySelector('.zooltjes-addon-data');
    let data = [];
    try { data = JSON.parse(node.textContent); } catch (e) { data = []; }
    return data.map((v) => {
      // Prefer the shoe-size range emitted by Liquid (letter sizes have no range in
      // the label); fall back to parsing a "38-41" range out of the size string.
      if (typeof v.min !== 'number' || typeof v.max !== 'number') {
        const m = String(v.size || '').match(/(\d+)\s*[-–]\s*(\d+)/);
        v.min = m ? parseInt(m[1], 10) : 0;
        v.max = m ? parseInt(m[2], 10) : 999;
      }
      return v;
    });
  }

  isMet() {
    return !!this.metRadio && this.metRadio.checked;
  }

  updateLabel() {
    if (!this.label) return;
    const checked = this.querySelector('.zooltjes-addon__radio:checked');
    const lbl = checked ? this.querySelector(`label[for="${checked.id}"] span`) : null;
    if (lbl) this.label.textContent = lbl.textContent.trim();
  }

  forceBaseVariant() {
    const fs = this.scope.querySelector(
      'fieldset.product-form__input--pakket, fieldset[data-handle="pakket"]'
    );
    if (!fs) return;
    const radios = fs.querySelectorAll('input[type="radio"]');
    if (radios.length) {
      let base = null;
      radios.forEach((r) => { if (!/zoolt/i.test(r.value)) base = r; });
      if (base && !base.checked) { base.checked = true; base.dispatchEvent(new Event('change', { bubbles: true })); }
      return;
    }
    const sel = fs.querySelector('select');
    if (sel) {
      let baseVal = null;
      Array.from(sel.options).forEach((o) => { if (!/zoolt/i.test(o.value)) baseVal = o.value; });
      if (baseVal != null && sel.value !== baseVal) { sel.value = baseVal; sel.dispatchEvent(new Event('change', { bubbles: true })); }
    }
  }

  currentShoeSize() {
    const num = (val) => {
      const m = String(val == null ? '' : val).match(/^\D*(\d{1,3})\D*$/);
      return m ? parseInt(m[1], 10) : null;
    };
    // Preferred: the configured size option name.
    const opt = this.sizeOption;
    const el = this.scope.querySelector(
      `input[name="${opt}"]:checked, input[name="${opt}--sticky"]:checked,` +
      `select[name="options[${opt}]"], input[name="options[${opt}]"]:checked`
    );
    if (el && num(el.value) != null) return num(el.value);
    // Fallback: the size option name varies per product (e.g. "Maat" vs
    // "Schoenmaat"). Pick any checked variant option whose value is a plain number.
    const cands = this.scope.querySelectorAll(
      '.variations input[type="radio"]:checked, .variations select,' +
      'variant-selects input[type="radio"]:checked, variant-selects select'
    );
    for (const c of cands) {
      if (c.classList && c.classList.contains('zooltjes-addon__radio')) continue;
      const n = num(c.value);
      if (n != null) return n;
    }
    return null;
  }

  matchInsole(size) {
    if (!size) return null;
    return this.insoles.find((v) => v.available && size >= v.min && size <= v.max) || null;
  }

  syncMetAvailability() {
    if (!this.metRadio) return;
    const available = !!this.matchInsole(this.currentShoeSize());
    this.metRadio.disabled = !available;
    this.metRadio.classList.toggle('is-disabled', !available);
    if (!available && this.metRadio.checked && this.enkelRadio) {
      this.enkelRadio.checked = true;
      this.updateLabel();
      this.applyPrice();
    }
  }

  /* ---- PDP price display (shoe + net insole price) ---- */

  priceAmounts() {
    const nodes = this.scope.querySelectorAll(
      '.product-price-container ins .amount, [id^="price-"] ins .amount'
    );
    return Array.from(new Set(nodes));
  }

  parseCents(str) {
    if (!str) return null;
    let s = str.replace(/[^\d.,-]/g, '').trim();
    if (!s) return null;
    s = s.replace(/,-/, ',00').replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? null : Math.round(n * 100);
  }

  formatCents(cents) {
    const whole = Math.floor(cents / 100);
    const rem = cents % 100;
    const wholeStr = whole.toLocaleString('nl-NL');
    return rem === 0 ? wholeStr + ',-' : wholeStr + ',' + String(rem).padStart(2, '0');
  }

  captureBase() {
    const amount = this.priceAmounts()[0];
    if (!amount) return;
    const cents = this.parseCents(amount.textContent);
    if (cents == null) return;
    this.baseCents = cents;
    const pm = amount.textContent.match(/^(\D*)\d/);
    this.basePrefix = pm ? pm[1] : '';
  }

  applyPrice() {
    if (!this.showPrice || !this.surcharge || this.baseCents == null) return;
    const finalCents = this.isMet() ? this.baseCents + this.surcharge : this.baseCents;
    const text = (this.basePrefix || '') + this.formatCents(finalCents);
    this._applyingPrice = true;
    this.priceAmounts().forEach((a) => { if (a.textContent.trim() !== text.trim()) a.textContent = text; });
    setTimeout(() => { this._applyingPrice = false; }, 0);
  }

  initPriceLayer() {
    if (!this.showPrice || !this.surcharge) return;
    this.captureBase();
    this.applyPrice();
    const containers = new Set();
    this.priceAmounts().forEach((a) => {
      const c = a.closest('[id^="price-"]') || a.closest('.product-price-container');
      if (c) containers.add(c);
    });
    if (!containers.size) return;
    this.priceObserver = new MutationObserver(() => {
      if (this._applyingPrice) return;
      this.captureBase();
      this.applyPrice();
    });
    containers.forEach((c) => this.priceObserver.observe(c, { childList: true, subtree: true, characterData: true }));
  }

  /* ---- Add to cart ---- */

  async onSubmit(event) {
    if (!this.isMet()) return; // "Enkel" -> normal add of the plain shoe

    const idInput = this.form.querySelector('input[name="id"]') ||
      this.scope.querySelector('input[name="id"]');
    const shoeId = idInput ? Number(idInput.value) : null;
    if (!shoeId) return;

    const insole = this.matchInsole(this.currentShoeSize());
    if (!insole) return; // no insole in stock -> let the shoe add normally (met is disabled anyway)

    event.preventDefault();
    event.stopImmediatePropagation();

    const qtyInput = this.form.querySelector('[name="quantity"]');
    const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    const items = [
      { id: shoeId, quantity: qty },
      { id: Number(insole.id), quantity: qty, properties: { _bundle: String(shoeId) } }
    ];

    const buttons = this.form.querySelectorAll('.single-add-to-cart-button');
    buttons.forEach((b) => { b.classList.add('loading'); b.setAttribute('aria-disabled', 'true'); });

    try {
      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('cart/add.js failed: ' + res.status);
      this.afterAdd();
    } catch (err) {
      console.error('[Zooltjes] Multi-add mislukt, voeg alleen de schoen toe:', err);
      try {
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: shoeId, quantity: qty }] })
        });
        this.afterAdd();
      } catch (e2) {
        console.error('[Zooltjes] Fallback ook mislukt:', e2);
      }
    } finally {
      buttons.forEach((b) => { b.classList.remove('loading'); b.removeAttribute('aria-disabled'); });
    }
  }

  afterAdd() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const drawer = document.getElementById('Cart-Drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }
}

if (!customElements.get('zooltjes-addon')) {
  customElements.define('zooltjes-addon', ZooltjesAddon);
}
